import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SnakeGame.css';

const GRID_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_DIRECTION = { x: 1, y: 0 };
const GAME_SPEED = 150;

const SnakeGame = () => {
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [food, setFood] = useState({ x: 15, y: 15 });
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const directionRef = useRef(INITIAL_DIRECTION);
  const gameLoopRef = useRef(null);

  // Generate random food position
  const generateFood = useCallback(() => {
    const newFood = {
      x: Math.floor(Math.random() * GRID_SIZE),
      y: Math.floor(Math.random() * GRID_SIZE)
    };
    return newFood;
  }, []);

  // Check collision
  const checkCollision = useCallback((head, snakeBody) => {
    // Wall collision
    if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
      return true;
    }
    // Self collision
    for (let segment of snakeBody) {
      if (head.x === segment.x && head.y === segment.y) {
        return true;
      }
    }
    return false;
  }, []);

  // Game loop
  const gameLoop = useCallback(() => {
    if (isPaused || gameOver) return;

    setSnake(prevSnake => {
      const head = { ...prevSnake[0] };
      const newDirection = directionRef.current;
      
      head.x += newDirection.x;
      head.y += newDirection.y;

      // Check collision
      if (checkCollision(head, prevSnake)) {
        setGameOver(true);
        setIsPlaying(false);
        return prevSnake;
      }

      const newSnake = [head, ...prevSnake];

      // Check if food eaten
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => prev + 10);
        const newFood = generateFood();
        // Make sure food doesn't spawn on snake
        let foodOnSnake = true;
        while (foodOnSnake) {
          foodOnSnake = newSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y);
          if (foodOnSnake) {
            Object.assign(newFood, generateFood());
          }
        }
        setFood(newFood);
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
  }, [food, checkCollision, generateFood, isPaused, gameOver]);

  // Handle keyboard input
  const handleKeyPress = useCallback((e) => {
    if (!isPlaying || gameOver) return;

    const key = e.key;
    const currentDir = directionRef.current;

    // Prevent reversing into itself
    if (key === 'ArrowUp' && currentDir.y === 0) {
      directionRef.current = { x: 0, y: -1 };
      setDirection({ x: 0, y: -1 });
    } else if (key === 'ArrowDown' && currentDir.y === 0) {
      directionRef.current = { x: 0, y: 1 };
      setDirection({ x: 0, y: 1 });
    } else if (key === 'ArrowLeft' && currentDir.x === 0) {
      directionRef.current = { x: -1, y: 0 };
      setDirection({ x: -1, y: 0 });
    } else if (key === 'ArrowRight' && currentDir.x === 0) {
      directionRef.current = { x: 1, y: 0 };
      setDirection({ x: 1, y: 0 });
    } else if (key === ' ' || key === 'Space') {
      e.preventDefault();
      setIsPaused(prev => !prev);
    }
  }, [isPlaying, gameOver]);

  // Start game
  const startGame = () => {
    setSnake(INITIAL_SNAKE);
    setDirection(INITIAL_DIRECTION);
    directionRef.current = INITIAL_DIRECTION;
    setFood(generateFood());
    setGameOver(false);
    setScore(0);
    setIsPlaying(true);
    setIsPaused(false);
  };

  // Reset game
  const resetGame = () => {
    setSnake(INITIAL_SNAKE);
    setDirection(INITIAL_DIRECTION);
    directionRef.current = INITIAL_DIRECTION;
    setFood(generateFood());
    setGameOver(false);
    setScore(0);
    setIsPlaying(false);
    setIsPaused(false);
  };

  // Game loop effect
  useEffect(() => {
    if (isPlaying && !gameOver) {
      gameLoopRef.current = setInterval(gameLoop, GAME_SPEED);
    } else {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
        gameLoopRef.current = null;
      }
    }

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [isPlaying, gameOver, gameLoop]);

  // Keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  const getDirectionLabel = () => {
    if (direction.x === 1) return 'Right';
    if (direction.x === -1) return 'Left';
    if (direction.y === 1) return 'Down';
    if (direction.y === -1) return 'Up';
    return 'Static';
  };

  return (
    <div className="snake-game-container">
      <div className="snake-game-header">
        <h2>🐍 Snake Game</h2>
        <div className="game-score">Score: {score}</div>
        <div className="game-direction">Direction: {getDirectionLabel()}</div>
      </div>
      
      <div className="game-board">
        {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, index) => {
          const x = index % GRID_SIZE;
          const y = Math.floor(index / GRID_SIZE);
          const isSnakeHead = snake[0].x === x && snake[0].y === y;
          const isSnakeBody = snake.slice(1).some(segment => segment.x === x && segment.y === y);
          const isFood = food.x === x && food.y === y;

          return (
            <div
              key={index}
              className={`cell ${isSnakeHead ? 'snake-head' : ''} ${isSnakeBody ? 'snake-body' : ''} ${isFood ? 'food' : ''}`}
            />
          );
        })}
      </div>

      {!isPlaying && !gameOver && (
        <div className="game-overlay">
          <div className="game-message">
            <h3>🐍 Ready to Play?</h3>
            <p>Use arrow keys to control the snake</p>
            <button onClick={startGame} className="game-btn start-btn">
              Start Game
            </button>
          </div>
        </div>
      )}

      {gameOver && (
        <div className="game-overlay">
          <div className="game-message">
            <h3>Game Over! 🎮</h3>
            <p>Final Score: {score}</p>
            <div className="game-buttons">
              <button onClick={startGame} className="game-btn start-btn">
                Play Again
              </button>
              <button onClick={resetGame} className="game-btn reset-btn">
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {isPaused && isPlaying && (
        <div className="game-overlay">
          <div className="game-message">
            <h3>⏸️ Paused</h3>
            <p>Press Space to resume</p>
          </div>
        </div>
      )}

      <div className="game-controls">
        <div className="control-instructions">
          <p>🎮 Controls:</p>
          <div className="control-keys">
            <div className="control-row">
              <button 
                className="control-btn" 
                onClick={() => handleKeyPress({ key: 'ArrowUp' })}
                disabled={!isPlaying || gameOver}
              >
                ↑
              </button>
            </div>
            <div className="control-row">
              <button 
                className="control-btn" 
                onClick={() => handleKeyPress({ key: 'ArrowLeft' })}
                disabled={!isPlaying || gameOver}
              >
                ←
              </button>
              <button 
                className="control-btn" 
                onClick={() => handleKeyPress({ key: 'ArrowDown' })}
                disabled={!isPlaying || gameOver}
              >
                ↓
              </button>
              <button 
                className="control-btn" 
                onClick={() => handleKeyPress({ key: 'ArrowRight' })}
                disabled={!isPlaying || gameOver}
              >
                →
              </button>
            </div>
          </div>
          <p className="space-hint">Press Space to pause</p>
        </div>
      </div>
    </div>
  );
};

export default SnakeGame;

